import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, FilePlus } from "lucide-react";

import iiApi from "@/lib/incomingInspectionApi";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SOURCE_LABELS = {
  dimension: "Dimension", visual: "Visual", fitment: "Fitment",
  surface_finish: "Surface Finish", thread1: "Thread 1", thread2: "Thread 2",
  manual: "Manual",
};
const REGISTER_PATH = "/quality/incoming-inspection/ncr-register";

export default function IncomingInspectionNcrRegister() {
  const nav = useNavigate();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [ncrs, setNcrs] = useState(null);

  const load = async (params) => {
    setNcrs(null);
    try {
      const res = await iiApi.listNcrs(params);
      setNcrs(res.ncrs || []);
    } catch (err) {
      setNcrs([]);
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { load({}); }, []);

  const applyFilter = () => load({ from: from || undefined, to: to || undefined });
  const clearFilter = () => { setFrom(""); setTo(""); load({}); };

  return (
    <div className="space-y-6" data-testid="incoming-inspection-ncr-register">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Quality</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>NCR Register</h1>
          <p className="text-sm text-slate-500 mt-1">Every Non-Conformance Report raised from Incoming/In-Process Inspection, most recent first.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            className="bg-red-600 hover:bg-red-700"
            onClick={() => nav(`/quality/incoming-inspection/ncr?${new URLSearchParams({ back: REGISTER_PATH })}`)}
            data-testid="raise-ncr-btn"
          >
            <FilePlus className="w-4 h-4 mr-2" />Raise NCR
          </Button>
          <Link to="/quality/incoming-inspection"><Button className="bg-blue-600 hover:bg-blue-700">New Inspection</Button></Link>
        </div>
      </div>

      <section className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div><Label>From Date</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><Label>To Date</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <Button onClick={applyFilter}>Apply Filter</Button>
        <Button variant="outline" onClick={clearFilter}>Clear</Button>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Non-Conformance Reports</div>
        {ncrs === null && <div className="py-10 text-center text-sm text-slate-500">Loading…</div>}
        {ncrs && ncrs.length === 0 && (
          <div className="py-14 text-center text-slate-400">
            <ClipboardList className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm">No NCRs found for this date range.</p>
          </div>
        )}
        {ncrs && ncrs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-4">NCR No</th><th className="py-2 px-4">Report</th><th className="py-2 px-4">Source</th>
                  <th className="py-2 px-4">Part Number</th><th className="py-2 px-4">Raised By</th><th className="py-2 px-4">Date</th>
                  <th className="py-2 px-4">Status</th><th className="py-2 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {ncrs.map((n) => {
                  const isClosed = n.status === "CLOSED";
                  const badgeCls = !n.is_complete ? "bg-slate-100 text-slate-500" : isClosed ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700";
                  const badgeLabel = !n.is_complete ? "Draft" : isClosed ? "Closed" : "Open";
                  const source = n.source === "dimension" ? `Dimension #${n.dimension_index ?? ""}` : SOURCE_LABELS[n.source] || n.source;
                  const qs = n.report_id
                    ? new URLSearchParams({ report_id: n.report_id, source: n.source, back: REGISTER_PATH })
                    : new URLSearchParams({ ncr_id: n.id, back: REGISTER_PATH });
                  if (n.report_id && n.dimension_index) qs.set("dimension_index", String(n.dimension_index));
                  return (
                    <tr key={n.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-4 font-mono font-semibold text-blue-700">{n.ncr_no}</td>
                      <td className="py-2 px-4">
                        {n.report_id
                          ? <Link to={`/quality/incoming-inspection/history/${n.report_id}`} className="text-slate-700 hover:underline">{n.report_title || n.report_id}</Link>
                          : <span className="text-slate-500 italic">Standalone</span>}
                      </td>
                      <td className="py-2 px-4">{source}</td>
                      <td className="py-2 px-4">{n.part_number || "—"}</td>
                      <td className="py-2 px-4">{n.raised_by_name || "—"}</td>
                      <td className="py-2 px-4">{n.created_at ? new Date(n.created_at).toLocaleDateString() : "—"}</td>
                      <td className="py-2 px-4"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badgeCls}`}>{badgeLabel}</span></td>
                      <td className="py-2 px-4">
                        <a href={`/quality/incoming-inspection/ncr?${qs.toString()}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-600 hover:underline">Open</a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
